import {
  createAppSelector,
  orgSelectors,
  orgUserSelectors,
  pageSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import { selectCurrentOrgRole, selectOrgRoleByUserId } from "./org";
import { selectPageGroupForReadyUser } from "./page-groups";
import {
  selectCurrentSectionRole,
  selectEnabledSectionUsers,
  selectSectionRoleBySectionId,
  selectSectionRoleByUserId,
} from "./section-user";
import { selectIsStudentPreview } from "./view";

import type {
  OrgRoleType,
  SectionRoleType,
  SectionUserType,
  UserType,
} from "components/server-types";
import type { RootState } from "store";
import { isGroupPage } from "utils/page-utils";
import { isMpOrg } from "utils/urls";

export enum UserRoleType {
  ORG_ADMIN = "orgAdmin",
  INSTRUCTOR = "instructor",
  STUDENT = "student",
  SECTION_ADMIN = "section_admin",
  MP_ADMIN = "mpAdmin",
  RECORDING_AGENT = "recordingAgent",
  UNKNOWN = "unknown",
}

// Capabilities at the page level
export enum PageCapability {
  CAN_VIEW_PAGE = "canViewPage",
  CAN_MAKE_RESPONSE = "canMakeResponse",
  CAN_AUTHOR_PAGE = "canAuthorPage",
}

// Capabilities at the section level
export enum SectionCapability {
  CAN_AUTHOR_SECTION = "canAuthorSection",
  CAN_EDIT_STUDENT_DRAFT = "canEditStudentDraft",
  CAN_CREATE_CHAT_MESSAGE = "canCreateChatMessage",
  CAN_DELETE_CHAT_MESSAGE = "canDeleteChatMessage",
  CAN_PARTICIPATE_IN_SECTION = "canParticipateInSection",
  CAN_PRESENT_SECTION = "canPresentSection",
  CAN_START_AND_STOP_RECORDING = "canStartAndStopRecording",
  IS_VISIBLE_IN_CLASS = "isVisibleInClass",
}

// Capabilities at the org level
export enum OrgCapability {
  CAN_ACCESS_MONITORING_DASHBOARD = "canAccessMonitoringDashboard",
  CAN_AUTHOR_ORG = "canAuthorOrg",
  CAN_OBSERVE_VIRTUAL_CLASS = "canObserveVirtualClass",
  CAN_SEE_ALL_DOCS = "canSeeAllDocs",
  CAN_SEE_ALL_ORG_USERS = "canSeeAllOrgUsers",
}

export const Capability = {
  ...SectionCapability,
  ...OrgCapability,
};

const ROLES_MAP: Record<SectionRoleType | "admin" | "recording_agent" | "other", UserRoleType> = {
  admin: UserRoleType.ORG_ADMIN,
  instructor: UserRoleType.INSTRUCTOR,
  section_admin: UserRoleType.SECTION_ADMIN,
  student: UserRoleType.STUDENT,
  recording_agent: UserRoleType.RECORDING_AGENT,
  other: UserRoleType.UNKNOWN,
};

const SECTION_CAPABILITIES_MAP: Record<SectionCapability, UserRoleType[]> = {
  [Capability.CAN_AUTHOR_SECTION]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_EDIT_STUDENT_DRAFT]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_CREATE_CHAT_MESSAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
    UserRoleType.STUDENT,
  ],
  [Capability.CAN_DELETE_CHAT_MESSAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_PARTICIPATE_IN_SECTION]: [UserRoleType.STUDENT],
  // For now, only instructors/section admins can present
  [Capability.CAN_PRESENT_SECTION]: [UserRoleType.INSTRUCTOR, UserRoleType.SECTION_ADMIN],
  [Capability.CAN_START_AND_STOP_RECORDING]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.IS_VISIBLE_IN_CLASS]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.STUDENT,
  ],
};

const ORG_CAPABILITIES_MAP: Record<OrgCapability, UserRoleType[]> = {
  // TODO: remove org admin capability once mp admins have a none org role
  [Capability.CAN_ACCESS_MONITORING_DASHBOARD]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
  [Capability.CAN_AUTHOR_ORG]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
  [Capability.CAN_SEE_ALL_DOCS]: [
    UserRoleType.ORG_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.RECORDING_AGENT,
  ],
  [Capability.CAN_SEE_ALL_ORG_USERS]: [UserRoleType.MP_ADMIN],
  [Capability.CAN_OBSERVE_VIRTUAL_CLASS]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
};

// Note: for students you need to actually be in the section to view the page so the org role
// does not matter. The only roles that can make a response are students on an individual page
// or are in a group and ready for a group page.
const PAGE_CAPABILITIES_MAP: Record<PageCapability, UserRoleType[]> = {
  [PageCapability.CAN_VIEW_PAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
    UserRoleType.RECORDING_AGENT,
  ],
  [PageCapability.CAN_AUTHOR_PAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [PageCapability.CAN_MAKE_RESPONSE]: [], // Only student which we handle separately
};

const getUserRole = (
  isMpAdmin: boolean,
  sectionRole: SectionRoleType | null,
  orgRole: OrgRoleType | null,
  isStudentPreview: boolean = false,
): UserRoleType => {
  if (isStudentPreview) {
    return UserRoleType.STUDENT;
  }
  const role = sectionRole || orgRole;
  // MP Admin users might have a "none" role in the org, which allows them to have full MP Admin capabilities.
  if (role && role !== "none") {
    return ROLES_MAP[role];
  }
  return isMpAdmin ? UserRoleType.MP_ADMIN : UserRoleType.UNKNOWN;
};

// Section capabilities
export const hasSectionCapability = (userRole: UserRoleType, capability: SectionCapability) =>
  SECTION_CAPABILITIES_MAP[capability].includes(userRole);

const getSectionCapabilities = (userRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(SectionCapability).map((capability) => [
      capability,
      hasSectionCapability(userRole, capability),
    ]),
  ) as Record<SectionCapability, boolean>;
};

// Org capabilities
export const hasOrgCapability = (userOrgRole: UserRoleType, capability: OrgCapability) => {
  return ORG_CAPABILITIES_MAP[capability].includes(userOrgRole);
};

const getOrgCapabilities = (userOrgRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(OrgCapability).map((capability) => [
      capability,
      hasOrgCapability(userOrgRole, capability),
    ]),
  ) as Record<OrgCapability, boolean>;
};

const hasPageCapability = (userRole: UserRoleType, capability: PageCapability) => {
  return PAGE_CAPABILITIES_MAP[capability].includes(userRole);
};

// This is really only useful for non-student roles since student's is specific to page state
const getPageCapabilities = (userRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(PageCapability).map((capability) => [
      capability,
      hasPageCapability(userRole, capability),
    ]),
  ) as Record<PageCapability, boolean>;
};

// TODO: This should be made more specific, e.g. canReadPage, canWritePage, etc., to avoid
// re-renders when the objects gets updated without causing any actual change.
export const selectCollaborationPermissionsForDoc = (state: RootState, docId: string | null) =>
  docId ? state.auth.websocket.pages?.[docId] : null;

export const selectShouldRequestAuth = (state: RootState) => state.auth.should_request_auth;

export const selectUserIsMpAdmin = createAppSelector(
  [(state, userId: string) => userId, (state) => state.users],
  (userId, users) => users[userId]?.is_superuser,
);

export const selectCurrentUserIsMPAdmin = createAppSelector(
  [selectCurrentUserId, (state) => state.users, selectIsStudentPreview],
  (userId, users, isStudentPreview) => Boolean(!isStudentPreview && users[userId!]?.is_superuser),
);

// Note: using this selector is discouraged, use capability selectors instead
export const selectCurrentUserRole = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectCurrentSectionRole,
    selectCurrentOrgRole,
    selectIsStudentPreview,
  ],
  (isMpAdmin, sectionRole, orgRole, isStudentPreview): UserRoleType =>
    getUserRole(isMpAdmin, sectionRole, orgRole, isStudentPreview),
);

export const selectCurrentUserOrgRole = createAppSelector(
  [selectCurrentUserIsMPAdmin, selectCurrentOrgRole],
  (isMpAdmin, orgRole): UserRoleType => getUserRole(isMpAdmin, null, orgRole),
);

export const selectUserRoleByUserId = createAppSelector(
  [selectUserIsMpAdmin, selectSectionRoleByUserId, selectOrgRoleByUserId],
  (isMpAdmin, sectionRole, orgRole): UserRoleType => getUserRole(isMpAdmin, sectionRole, orgRole),
);

export const selectCurrentUserRoleBySectionId = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    (state: RootState, sectionId: string) => selectSectionRoleBySectionId(state, sectionId),
    selectCurrentOrgRole,
  ],
  (isMpAdmin, sectionRole, orgRole): UserRoleType => getUserRole(isMpAdmin, sectionRole, orgRole),
);

export const selectIsMPOrg = createAppSelector([orgSelectors.selectAll], (orgs) =>
  isMpOrg(orgs.find((org) => org.current)),
);

export const selectSectionCapabilitiesBySectionId = createAppSelector(
  [(state: RootState, sectionId: string) => selectCurrentUserRoleBySectionId(state, sectionId)],
  (userRole) => getSectionCapabilities(userRole),
);

export const selectSectionCapabilities = createAppSelector([selectCurrentUserRole], (userRole) =>
  getSectionCapabilities(userRole),
);

export const selectOrgCapabilities = createAppSelector([selectCurrentUserOrgRole], (userOrgRole) =>
  getOrgCapabilities(userOrgRole),
);

// TODO: We should start using the page capabilities for more than just submission button availability.
export const selectUserPageCapabilities = createAppSelector(
  [
    pageSelectors.selectById,
    selectCurrentUserRole,
    selectCurrentUserOrgRole,
    (state, pageId: string, worksheetId: string | null) =>
      selectCollaborationPermissionsForDoc(state, worksheetId),
    (state: RootState, pageId: string) => selectPageGroupForReadyUser(state, pageId),
  ],
  (
    page,
    currentUserRole,
    currentOrgRole,
    permissions,
    readyPageGroup,
  ): Record<PageCapability, boolean> => {
    const { canParticipateInSection } = getSectionCapabilities(currentUserRole);
    const { canSeeAllDocs } = getOrgCapabilities(currentOrgRole);
    if (canParticipateInSection) {
      const hasPagePermissions = Boolean(permissions) || canSeeAllDocs;

      // If it's a group page user has to be in a group and ready to make a response
      const canUpdatePage = Boolean(
        hasPagePermissions && page && isGroupPage(page) ? readyPageGroup : hasPagePermissions,
      );

      const canMakeResponse = canUpdatePage && !page?.locked_at;

      return {
        [PageCapability.CAN_VIEW_PAGE]: hasPagePermissions,
        [PageCapability.CAN_MAKE_RESPONSE]: canMakeResponse,
        [PageCapability.CAN_AUTHOR_PAGE]: false,
      };
    }

    return getPageCapabilities(currentUserRole);
  },
);

export const selectSectionCapabilitiesByUserId = createAppSelector(
  [selectUserRoleByUserId],
  (userRole) => getSectionCapabilities(userRole),
);

export const selectIsRecordingAgent = createAppSelector(
  [selectCurrentUserRole],
  (userRole) => userRole === UserRoleType.RECORDING_AGENT,
);

const getSectionUsersWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => {
  if (!sectionId) {
    return [];
  }
  return sectionUsers.filter((sectionUser) => {
    if (sectionUser.section_id !== sectionId) {
      return false;
    }
    const sectionRole = sectionUser.role;
    return hasSectionCapability(ROLES_MAP[sectionRole], capability);
  });
};

export const getSectionUserIdsWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => getSectionUsersWithSectionCapability(sectionUsers, capability, sectionId).map(({ id }) => id);

export const selectSectionUserIdsWithSectionCapability = createAppSelector(
  [
    selectEnabledSectionUsers,
    selectCurrentSectionId,
    (state, capability: SectionCapability) => capability,
  ],
  (sectionUsers, sectionId, capability) =>
    getSectionUserIdsWithSectionCapability(sectionUsers, capability, sectionId),
);

// User role map in the current section
// This is used to determine who has what capabilities in the current section.
// TODO: consider using selectEnabledSectionUsers for this
export const selectUserRoleMapInCurrentSection = createAppSelector(
  [
    userSelectors.selectAll,
    sectionUserSelectors.filterForCurrentSection,
    orgUserSelectors.selectAll,
  ],
  (users, sectionUsers, orgUsers): Array<[UserType, UserRoleType]> => {
    // Users with a role in the section include:
    // - Section users with a role in the section
    // - Org users with a role in the org other than "none"
    // - Users with a system-wide role
    // but does not include disabled users at the section or org level

    const sectionUsersByUserId = new Map(
      sectionUsers.map((sectionUser) => [sectionUser.user_id, sectionUser]),
    );
    const orgUsersByUserId = new Map(orgUsers.map((ou) => [ou.user_id, ou]));
    const enabledUsers = users.filter((user) => {
      const sectionUser = sectionUsersByUserId.get(user.id);
      if (sectionUser && sectionUser.is_disabled) {
        return false;
      }

      const orgUser = orgUsersByUserId.get(user.id);
      if (orgUser && orgUser.is_disabled && orgUser.role !== "none") {
        return false;
      }

      return true;
    });

    return enabledUsers.map((user) => [
      user,
      getUserRole(
        user.is_superuser,
        sectionUsersByUserId.get(user.id)?.role || null,
        orgUsersByUserId.get(user.id)?.role || null,
        false,
      ),
    ]);
  },
);

// User with specific capabilities in the current section

const getUsersWithSectionCapability = (
  userRoleMap: Array<[UserType, UserRoleType]>,
  capability: SectionCapability,
) => {
  return userRoleMap
    .filter(([, role]) => hasSectionCapability(role, capability))
    .map(([user]) => user);
};

export const selectUsersWhoCanParticipateInSection = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) =>
    getUsersWithSectionCapability(userRoleMap, Capability.CAN_PARTICIPATE_IN_SECTION),
);

// User IDs with specific capabilities in the current section

const getUserIdsWithSectionCapability = (
  userRoleMap: Array<[UserType, UserRoleType]>,
  capability: SectionCapability,
) => {
  return getUsersWithSectionCapability(userRoleMap, capability).map((user) => user.id);
};

export const selectUserIdsWhoAreVisibleInClass = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) => getUserIdsWithSectionCapability(userRoleMap, Capability.IS_VISIBLE_IN_CLASS),
);

export const selectUserIdsWhoCanAuthorSection = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) => getUserIdsWithSectionCapability(userRoleMap, Capability.CAN_AUTHOR_SECTION),
);

export const selectUserIdsWhoCanCreateChatMessage = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) => getUserIdsWithSectionCapability(userRoleMap, Capability.CAN_CREATE_CHAT_MESSAGE),
);

export const selectUserIdsWhoCanParticipateInSection = createAppSelector(
  [selectUserRoleMapInCurrentSection, selectEnabledSectionUsers],
  (userRoleMap, enabledSectionUsers) => {
    const studentUserIds = getUserIdsWithSectionCapability(
      userRoleMap,
      Capability.CAN_PARTICIPATE_IN_SECTION,
    );

    // Filter out disabled section users
    return studentUserIds.filter((user_id) =>
      enabledSectionUsers.some((cu) => cu.user_id === user_id),
    );
  },
);

// User counts with specific capabilities in the current section

export const selectCountUsersWhoCanParticipateInSection = createAppSelector(
  [selectUserIdsWhoCanParticipateInSection],
  (userIds) => userIds.length,
);
